import classnames from 'classnames';

import { isDefined, isFunction } from '@enterprise/core';
import { useDeepCompareMemo, useElementSize } from '@enterprise/hooks';

import type { LoaderProps } from './types/loader.props';
import type { LoadingProps } from './types/loading.props';

import { DualRing } from './dual-ring.component';
import { Glow } from './glow.component';
import { Grid } from './grid.component';
import { Ring } from './ring.component';

import cssClasses from './loading.module.css';

/**
 * Displays a loading indicator overlay with optional message and style.
 *
 * Renders one of several loader types (`dual-ring`, `glow`, `grid`, or `ring`) based on the `type` prop.
 * Can be displayed as a full-screen overlay or over a specific target element.
 *
 * @param {LoadingProps} props - The props for the Loading component.
 * @param {boolean} [props.fullScreen=true] - Whether to display the loader as a full-screen overlay.
 * Automatically set to `false` if `targetRef` is provided.
 * @param {string} [props.message='Loading'] - The message to display with the loader.
 * @param {number} [props.size=1] - The size multiplier for the loader.
 * @param {React.RefObject<HTMLElement>} [props.targetRef] - Reference to a target element to overlay the loader on.
 * If provided, disables full-screen mode.
 * @param {'dual-ring' | 'glow' | 'grid' | 'ring'} [props.type='grid'] - The type of loader to display.
 *
 * @returns {JSX.Element} The loading overlay component.
 */
export const Loading = ({
  fullScreen = true,
  message = 'Loading',
  size = 1,
  targetRef,
  type = 'grid',
}: LoadingProps) => {
  const props: LoaderProps = {
    message,
    size,
  };
  const isValidRef = isDefined(targetRef) && !isFunction(targetRef);

  const [, elementSize] = useElementSize(isValidRef ? targetRef : null);

  const { height, width } = useDeepCompareMemo(() => {
    return {
      height: elementSize?.height ?? 0,
      width: elementSize?.width ?? 0,
    };
  }, [elementSize]);

  fullScreen = isDefined(targetRef) ? false : fullScreen;

  return (
    <div
      className={classnames(cssClasses.loadingOverlay, { [cssClasses.fullScreen ?? '']: fullScreen })}
      style={{
        height: fullScreen ? undefined : height,
        width: fullScreen ? undefined : width,
      }}>
      {type === 'dual-ring' && <DualRing {...props} />}
      {type === 'glow' && <Glow {...props} />}
      {type === 'grid' && <Grid {...props} />}
      {type === 'ring' && <Ring {...props} />}
    </div>
  );
};
